import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  BarChart3,
  Pill,
} from "lucide-react";

interface PharmacySidebarProps {
  activeTab: string;
  onNavigateToTab: (tab: string) => void;
  pharmacyName?: string;
}

interface BadgeCounts {
  receivedRx: number;
}

const navGroups = [
  {
    label: "Core",
    items: [
      { id: "overview", label: "Overview", icon: BarChart3 },
    ],
  },
  {
    label: "Prescriptions",
    items: [
      { id: "prescriptions", label: "All Prescriptions", icon: Pill, countKey: "receivedRx" as const },
    ],
  },
];

const PharmacySidebar = ({ activeTab, onNavigateToTab, pharmacyName }: PharmacySidebarProps) => {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const collapsed = state === "collapsed";
  const { user } = useAuth();
  const [counts, setCounts] = useState<BadgeCounts>({ receivedRx: 0 });

  useEffect(() => {
    if (!user) return;
    const fetchCounts = async () => {
      const { count } = await supabase
        .from("prescriptions")
        .select("id", { count: "exact", head: true })
        .eq("status", "sent");
      setCounts({ receivedRx: count || 0 });
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <Sidebar collapsible="icon" className="border-r">
      <SidebarHeader className="p-3">
        <div className="px-3 py-1.5">
          {!collapsed && (
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {pharmacyName || "Pharmacy"}
            </span>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {navGroups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-semibold">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const count = item.countKey ? counts[item.countKey] : 0;
                  const isActive = activeTab === item.id;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => {
                          onNavigateToTab(item.id);
                          if (isMobile) setOpenMobile(false);
                        }}
                        isActive={isActive}
                        tooltip={item.label}
                        className="cursor-pointer"
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                      {count > 0 && !collapsed && (
                        <SidebarMenuBadge className="bg-destructive/10 text-destructive text-[10px] font-semibold">
                          {count}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
};

export default PharmacySidebar;
